import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import { kioskApi, loadKiosk, saveKiosk, clearKiosk, type KioskState } from './kiosk-api'
import KioskPareamento from './KioskPareamento'
import KioskLogin from './KioskLogin'
import KioskHome from './KioskHome'

type Step = 'pareamento' | 'login' | 'home'

export default function KioskRoot() {
  const { token: tokenUrl } = useParams<{ token?: string }>()
  const [state, setState] = useState<KioskState>(loadKiosk())
  const [step, setStep] = useState<Step>('pareamento')
  const [loading, setLoading] = useState(false)

  // Se chegou via /kiosk/:token, pareia automaticamente
  useEffect(() => {
    if (tokenUrl && tokenUrl !== state.machine_token) {
      doParear(tokenUrl)
    }
  }, [tokenUrl])

  // Decide step inicial
  useEffect(() => {
    if (!state.machine_token || !state.machine) setStep('pareamento')
    else if (!state.operator_id) setStep('login')
    else setStep('home')
  }, [state.machine_token, state.machine, state.operator_id])

  // Heartbeat 30s — só quando pareado
  useEffect(() => {
    if (!state.machine_token) return
    const tick = () => kioskApi.post('/kiosk/heartbeat').catch(() => {})
    tick()
    const iv = setInterval(tick, 30_000)
    return () => clearInterval(iv)
  }, [state.machine_token])

  async function doParear(token: string) {
    setLoading(true)
    try {
      saveKiosk({ machine_token: token })
      const device_info = `${navigator.userAgent.slice(0, 80)} / ${window.screen.width}x${window.screen.height}`
      const r = await kioskApi.post('/kiosk/parear', { token, device_info })
      const next = saveKiosk({ machine: r.data })
      setState(next)
      toast.success(`Máquina ${r.data.codigo} pareada`)
    } catch (e: any) {
      clearKiosk()
      setState(loadKiosk())
      toast.error(e?.response?.data?.detail || 'Token inválido')
    } finally {
      setLoading(false)
    }
  }

  async function doLogin(pin: string) {
    setLoading(true)
    try {
      saveKiosk({ operator_pin: pin })
      const r = await kioskApi.post('/kiosk/login-operador', { pin })
      const next = saveKiosk({
        operator_id: r.data.id,
        operator_nome: r.data.nome,
        operator_pin: pin,
        turno_id: r.data.turno_atual_id,
        turno_nome: r.data.turno_atual_nome,
      })
      setState(next)
      toast.success(`Olá, ${r.data.nome}`)
    } catch (e: any) {
      saveKiosk({ operator_pin: null })
      toast.error(e?.response?.data?.detail || 'PIN incorreto')
    } finally {
      setLoading(false)
    }
  }

  function logoutOperator() {
    const next = saveKiosk({ operator_id: null, operator_nome: null, operator_pin: null })
    setState(next)
  }

  function unpair() {
    if (!confirm('Desparear esta máquina? Vai precisar do token novamente.')) return
    clearKiosk()
    setState(loadKiosk())
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-ink-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-violet-600" />
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-ink-50 overflow-auto">
      {step === 'pareamento' && <KioskPareamento onParear={doParear} />}
      {step === 'login' && state.machine && (
        <KioskLogin machine={state.machine} onLogin={doLogin} onUnpair={unpair} />
      )}
      {step === 'home' && state.machine && (
        <KioskHome
          state={state}
          onLogout={logoutOperator}
          onUnpair={unpair}
          onStateChange={setState}
        />
      )}
    </div>
  )
}
